import styles from './Content.module.css';
import DesignSettings from './TabSections/DesignSettings';
import GeneralSettings from './TabSections/GeneralSettings';
import PropertiesSettings from './TabSections/PropertiesSettings';
import PropertyDetailsSettings from './TabSections/PropertyDetailsSettings';
import SearchSettings from './TabSections/SearchSettings';
import ShortcodesSettings from './TabSections/ShortcodesSettings';
import CurrencySettings from './TabSections/TabDropdown/CurrencySettings';
import MiscSettingsSettings from './TabSections/TabDropdown/MiscSettingsSettings';
import SearchPropertyResultSettings from './TabSections/TabDropdown/SearchPropertyResultSettings';
import SearchPropertySettings from './TabSections/TabDropdown/SearchPropertySettings';

// Filter currency-specific fields from general settings
const getCurrencyFields = (settingsData) => {
  if (!settingsData) return {};
  
  const currencyFields = {};
  const currencyKeys = [
    'hvnly_EnabledCurrencyFormat',
    'hvnly_thousandSeparator',
    'hvnly_decimalSeparator',
    'hvnly_numberOfDecimals',
    'hvnly_thousandText',
    'hvnly_millionText',
    'hvnly_billionText',
    'hvnly_currencyType',
    'hvnly_currencyPositionType',
    'hvnly_priceOnCallText',
    'hvnly_priceFormat'
  ];
  
  currencyKeys.forEach(key => {
    if (settingsData[key] !== undefined) {
      currencyFields[key] = settingsData[key];
    }
  });
  
  return currencyFields;
};

// Filter misc settings fields from general settings
const getMiscFields = (settingsData) => {
  if (!settingsData) return {};
  
  const miscFields = {};
  const miscKeys = [
    'hvnly_EnabledGutenbergEditor',
    'hvnly_EnabledUserReviews',
    'hvnly_EnabledGuestReviews',
    'hvnly_EnabledUserFavorites',
    'hvnly_EnabledSocialShare',
    'hvnly_defaultCity',
    'hvnly_defaultState',
    'hvnly_countryType',
    'hvnly_enableSocialShare'
  ];
  
  miscKeys.forEach(key => {
    if (settingsData[key] !== undefined) {
      miscFields[key] = settingsData[key];
    }
  });
  
  return miscFields;
};

// FIXED: Get ALL search settings (not just 2 fields)
const getSearchPropertyFields = (settingsData) => {
  if (!settingsData) return {};
  
  // Return the entire settingsData because it already contains all search fields
  // This includes: searchBarTitle, searchButtonText, hideTopSearchTitle, 
  // hideTopSearchBar, hideLeftSearchBar, enableAjaxLoadMore, etc.
  return settingsData;
};

// Filter search result fields from search settings
const getSearchResultFields = (settingsData) => {
  if (!settingsData) return {};
  
  const searchResultFields = {};
  const searchResultKeys = [
    'hvnly_hideTopSearchTitle',
    'hvnly_hideTopSearchBar',
    'hvnly_hideLeftSearchBar',
    'hvnly_numberOfColumns',
    'hvnly_searchResultPerpage',
    'hvnly_sidebarLayoutType'
  ];
  
  searchResultKeys.forEach(key => {
    if (settingsData[key] !== undefined) {
      searchResultFields[key] = settingsData[key];
    }
  });
  
  return searchResultFields;
};

const Content = ({ activeTab, onSaveTab, onResetTab, settingsData, onSettingsChange, showToast }) => {
  // console.log('🔵 [Content] COMPONENT RENDERED');
  // console.log('🔵 [Content] Active Tab:', activeTab);
  // console.log('🔵 [Content] settingsData received:', settingsData);

  const renderContent = () => {
    switch (activeTab) {
      case 'currency':
        // console.log('🔵 [Content] Rendering CurrencySettings');
        return (
          <CurrencySettings 
            key="currency-settings"
            onSave={(data) => {
              // console.log('📤 [Content] CurrencySettings onSave called with data:', data);
              onSaveTab('general', data);
            }}
            onReset={() => {
              // console.log('🔄 [Content] CurrencySettings onReset called');
              onResetTab('general');
            }}
            settingsData={getCurrencyFields(settingsData)}
            onSettingsChange={(newData) => {
              // console.log('📝 [Content] CurrencySettings onSettingsChange called with:', newData);
              onSettingsChange('currency', newData);
            }}
            showToast={showToast}
          />
        );
      case 'misc-settings':
        // console.log('🔵 [Content] Rendering MiscSettingsSettings');
        return (
          <MiscSettingsSettings 
            key="misc-settings"
            onSave={(data) => {
              // console.log('📤 [Content] MiscSettingsSettings onSave called with data:', data);
              onSaveTab('general', data);
            }}
            onReset={() => {
              // console.log('🔄 [Content] MiscSettingsSettings onReset called');
              onResetTab('general');
            }}
            settingsData={getMiscFields(settingsData)}
            onSettingsChange={(newData) => {
              // console.log('📝 [Content] MiscSettingsSettings onSettingsChange called with:', newData);
              onSettingsChange('misc-settings', newData);
            }}
            showToast={showToast}
          />
        );
      case 'search-property':
        // console.log('🔵 [Content] Rendering SearchPropertySettings');
        return (
          <SearchPropertySettings 
            key="search-property"
            onSave={(data) => {
              // console.log('📤 [Content] SearchPropertySettings onSave called with data:', data);
              onSaveTab('search', data);
            }}
            onReset={() => {
              // console.log('🔄 [Content] SearchPropertySettings onReset called');
              onResetTab('search');
            }}
            settingsData={getSearchPropertyFields(settingsData)}
            onSettingsChange={(newData) => {
              // console.log('📝 [Content] SearchPropertySettings onSettingsChange called with:', newData);
              onSettingsChange('search-property', newData);
            }}
            showToast={showToast}
          />
        );
      case 'search-result':
        // console.log('🔵 [Content] Rendering SearchPropertyResultSettings');
        return (
          <SearchPropertyResultSettings 
            key="search-result"
            onSave={(data) => {
              // console.log('📤 [Content] SearchPropertyResultSettings onSave called with data:', data);
              onSaveTab('search', data);
            }}
            onReset={() => {
              // console.log('🔄 [Content] SearchPropertyResultSettings onReset called');
              onResetTab('search');
            }}
            settingsData={getSearchResultFields(settingsData)}
            onSettingsChange={(newData) => {
              // console.log('📝 [Content] SearchPropertyResultSettings onSettingsChange called with:', newData);
              onSettingsChange('search-result', newData);
            }}
            showToast={showToast}
          />
        );
      case 'design':
        // console.log('🔵 [Content] Rendering DesignSettings');
        return (
          <DesignSettings 
            key="design-settings"
            onSave={(data) => {
              // console.log('📤 [Content] DesignSettings onSave called with data:', data);
              onSaveTab('design', data);
            }}
            onReset={() => {
              // console.log('🔄 [Content] DesignSettings onReset called');
              onResetTab('design');
            }}
            settingsData={settingsData}
            onSettingsChange={(newData) => {
              // console.log('📝 [Content] DesignSettings onSettingsChange called with:', newData);
              onSettingsChange('design', newData);
            }}
            showToast={showToast}
          />
        );
      case 'search':
        // console.log('🔵 [Content] Rendering SearchSettings');
        return (
          <SearchSettings 
            key="search-settings"
            onSave={(data) => {
              // console.log('📤 [Content] SearchSettings onSave called with data:', data);
              onSaveTab('search', data);
            }}
            onReset={() => {
              // console.log('🔄 [Content] SearchSettings onReset called');
              onResetTab('search');
            }}
            settingsData={settingsData}
            onSettingsChange={(newData) => {
              // console.log('📝 [Content] SearchSettings onSettingsChange called with:', newData);
              onSettingsChange('search', newData);
            }}
            showToast={showToast}
          />
        );
      case 'properties':
        // console.log('🔵 [Content] Rendering PropertiesSettings');
        return (
          <PropertiesSettings 
            key="properties-settings"
            onSave={(data) => {
              // console.log('📤 [Content] PropertiesSettings onSave called with data:', data);
              onSaveTab('properties', data);
            }}
            onReset={() => {
              // console.log('🔄 [Content] PropertiesSettings onReset called');
              onResetTab('properties');
            }}
            settingsData={settingsData}
            onSettingsChange={(newData) => {
              // console.log('📝 [Content] PropertiesSettings onSettingsChange called with:', newData);
              onSettingsChange('properties', newData);
            }}
            showToast={showToast}
          />
        );
      case 'property-details':
        // console.log('🔵 [Content] Rendering PropertyDetailsSettings');
        return (
          <PropertyDetailsSettings 
            key="property-details"
            onSave={(data) => {
              // console.log('📤 [Content] PropertyDetailsSettings onSave called with data:', data);
              onSaveTab('property-details', data);
            }}
            onReset={() => {
              // console.log('🔄 [Content] PropertyDetailsSettings onReset called');
              onResetTab('property-details');
            }}
            settingsData={settingsData}
            onSettingsChange={(newData) => {
              // console.log('📝 [Content] PropertyDetailsSettings onSettingsChange called with:', newData);
              onSettingsChange('property-details', newData);
            }}
            showToast={showToast}
          />
        );
      case 'shortcodes':
        // console.log('🔵 [Content] Rendering ShortcodesSettings');
        return (
          <ShortcodesSettings 
            key="shortcodes-settings"
            onSave={(data) => {
              // console.log('📤 [Content] ShortcodesSettings onSave called with data:', data);
              onSaveTab('shortcodes', data);
            }}
            onReset={() => {
              // console.log('🔄 [Content] ShortcodesSettings onReset called');
              onResetTab('shortcodes');
            }}
            settingsData={settingsData}
            onSettingsChange={(newData) => {
              // console.log('📝 [Content] ShortcodesSettings onSettingsChange called with:', newData);
              onSettingsChange('shortcodes', newData);
            }}
            showToast={showToast}
          />
        );
      default:
        // console.log('🔵 [Content] Rendering GeneralSettings (default)');
        return (
          <GeneralSettings 
            key="general-settings"
            onSave={(data) => {
              // console.log('📤 [Content] GeneralSettings onSave called with data:', data);
              onSaveTab('general', data);
            }}
            onReset={() => {
              // console.log('🔄 [Content] GeneralSettings onReset called');
              onResetTab('general');
            }}
            settingsData={settingsData}
            onSettingsChange={(newData) => {
              // console.log('📝 [Content] GeneralSettings onSettingsChange called with:', newData);
              onSettingsChange('general', newData);
            }}
            showToast={showToast}
          />
        );
    }
  };

  return (
    <div className={styles['hvnly-settings-api__contents']}>
      {renderContent()}
    </div>
  );
};

export default Content;